import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PageHeader from '@/components/PageHeader';
import VariboxDetailsDialog from '@/components/VariboxDetailsDialog';
import VariboxStatusBadge from '@/components/VariboxStatusBadge';
import { useAuth } from '@/auth/AuthProvider';
import { useI18n } from '@/i18n';
import { getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { assignScannedVariboxLocation, mapApiVariboxToView, type VariboxViewRecord } from '@/services/variboxApi';
import { findVariboxByBarcode } from '@/utils/variboxScan';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

type ScanStatus = 'ready' | 'scanning' | 'found' | 'notFound' | 'error';
type WorkflowAction = 'camera' | null;
type CameraScanMode = 'barcode' | 'qr';

const ONE_DIMENSIONAL_FORMATS = [
  BarcodeFormat.CODABAR,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.ITF,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.RSS_14,
  BarcodeFormat.RSS_EXPANDED,
];

interface ScanHistoryEntry {
  barcode: string;
  varibox: string;
  time: string;
  result: 'found' | 'notFound' | 'error';
}

type LocationOption = {
  id: string;
  parentId: string | null;
  label: string;
  depth: number;
  kind: string;
  rootCode: string | null;
};

const MAX_HISTORY_ITEMS = 5;

function getStatusColor(status: ScanStatus) {
  switch (status) {
    case 'found':
      return 'success';
    case 'notFound':
      return 'error';
    case 'error':
      return 'warning';
    case 'scanning':
      return 'info';
    default:
      return 'default';
  }
}

function formatLocationLabel(item: ApiLocationNode) {
  if (item.code && item.code.trim().toLowerCase() === item.name.trim().toLowerCase()) {
    return item.name.trim();
  }

  const codePart = item.code ? `${item.code} ` : '';
  return `${codePart}${item.name}`.trim();
}

function flattenLocations(nodes: Awaited<ReturnType<typeof getLocationTree>>['items']): LocationOption[] {
  const flattened: LocationOption[] = [];

  const walk = (items: typeof nodes, depth: number, parentId: string | null, rootCode: string | null) => {
    for (const item of items) {
      const prefix = depth > 0 ? `${'  '.repeat(depth)}- ` : '';
      const currentRootCode = depth === 0 ? item.code : rootCode;
      flattened.push({
        id: item.id,
        parentId,
        depth,
        kind: item.kind,
        rootCode: currentRootCode,
        label: `${prefix}${formatLocationLabel(item)}`.trim(),
      });
      walk(item.children, depth + 1, item.id, currentRootCode);
    }
  };

  walk(nodes, 0, null, null);
  return flattened;
}

function isUnderRoot(option: LocationOption, rootId: string, optionMap: Map<string, LocationOption>) {
  let currentParent = option.parentId;
  while (currentParent) {
    if (currentParent === rootId) {
      return true;
    }
    currentParent = optionMap.get(currentParent)?.parentId ?? null;
  }
  return false;
}

export default function ScanPage() {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin = user?.role === 'Admin';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const scanDebounceRef = useRef<number | null>(null);
  const lastFoundSerialRef = useRef<string | null>(null);
  const autoCameraStartedRef = useRef(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [status, setStatus] = useState<ScanStatus>('ready');
  const [lastBarcode, setLastBarcode] = useState('—');
  const [foundRecord, setFoundRecord] = useState<VariboxViewRecord | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistoryEntry[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsHistoryVisible, setDetailsHistoryVisible] = useState(true);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const manualBarcodeRef = useRef<HTMLInputElement | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState<CameraScanMode>('barcode');
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const cameraControlsRef = useRef<IScannerControls | null>(null);
  const [workflowAction, setWorkflowAction] = useState<WorkflowAction>(null);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [selectedRootLocationId, setSelectedRootLocationId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [assigningLocation, setAssigningLocation] = useState(false);
  const [assignLocationMessage, setAssignLocationMessage] = useState('');
  const [assignLocationError, setAssignLocationError] = useState('');

  const locale = language === 'pl' ? 'pl-PL' : 'en-GB';

  const locationById = useMemo(() => {
    const map = new Map<string, LocationOption>();
    for (const option of locationOptions) {
      map.set(option.id, option);
    }
    return map;
  }, [locationOptions]);

  const rootLocationOptions = useMemo(() => {
    return locationOptions.filter((option) => (option.depth === 0 || option.kind === 'root') && (option.rootCode === 'PL01' || option.rootCode === 'PL99'));
  }, [locationOptions]);

  const sublocationOptions = useMemo(() => {
    if (!selectedRootLocationId) {
      return [];
    }

    return locationOptions.filter((option) => option.depth > 0 && isUnderRoot(option, selectedRootLocationId, locationById));
  }, [locationById, locationOptions, selectedRootLocationId]);


  const statusLabel = useMemo(() => {
    return t(`scan.status.${status}`);
  }, [status, t]);


  const closeCameraScanner = () => {
    cameraControlsRef.current?.stop();
    cameraControlsRef.current = null;

    cameraReaderRef.current = null;

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    setCameraLoading(false);
  };

  const openCameraScanner = (mode: CameraScanMode = 'barcode') => {
    setWorkflowAction(null);
    setCameraError('');
    setCameraMode(mode);
    setCameraOpen(true);
  };

  const restartCameraScanner = () => {
    closeCameraScanner();
    window.setTimeout(() => {
      setCameraError('');
      setCameraOpen(true);
    }, 0);
  };

  useEffect(() => {
    if (!isMobile) {
      inputRef.current?.focus();
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !user || user.role === 'Admin' || autoCameraStartedRef.current) {
      return;
    }

    autoCameraStartedRef.current = true;
    setWorkflowAction(null);
    setCameraError('');
    setCameraOpen(true);
  }, [isMobile, user]);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      try {
        const data = await getLocationTree();
        if (!cancelled) {
          setLocationOptions(flattenLocations(data.items));
        }
      } catch {
        if (!cancelled) {
          setLocationOptions([]);
        }
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cameraOpen) {
      return;
    }

    let cancelled = false;

    const stopCamera = () => {
      cameraControlsRef.current?.stop();
      cameraControlsRef.current = null;

      cameraReaderRef.current = null;

      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
    };

    const startCamera = async () => {
      setCameraLoading(true);
      setCameraError('');

      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('unsupported');
        }

        const video = cameraVideoRef.current;
        if (!video) {
          throw new Error('video-not-ready');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
          },
          audio: false,
        });

        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }

        video.srcObject = stream;
        await video.play();

        const hints = new Map<DecodeHintType, unknown>();
        hints.set(
          DecodeHintType.POSSIBLE_FORMATS,
          cameraMode === 'qr' ? [BarcodeFormat.QR_CODE] : ONE_DIMENSIONAL_FORMATS,
        );
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints);
        cameraReaderRef.current = reader;

        const controls = await reader.decodeFromVideoElement(
          video,
          (result) => {
            if (!result) {
              return;
            }

            const format = result.getBarcodeFormat();
            if (cameraMode === 'qr' ? format !== BarcodeFormat.QR_CODE : format === BarcodeFormat.QR_CODE) {
              return;
            }

            const detected = result?.getText().trim();
            if (!detected) {
              return;
            }

            closeCameraScanner();
            runScan(detected);
          },
        );

        cameraControlsRef.current = controls;
      } catch {
        if (!cancelled) {
          setCameraError(t('scan.camera.unavailable'));
        }
      } finally {
        if (!cancelled) {
          setCameraLoading(false);
        }
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      stopCamera();
      setCameraLoading(false);
    };
  }, [cameraMode, cameraOpen, t]);

  useEffect(() => {
    if (!manualEntryOpen) {
      return;
    }

    window.setTimeout(() => {
      manualBarcodeRef.current?.focus();
      manualBarcodeRef.current?.select();
    }, 0);
  }, [manualEntryOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      if (scanDebounceRef.current) {
        window.clearTimeout(scanDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const serial = foundRecord?.serialNumber ?? null;

    if (!serial || serial === lastFoundSerialRef.current) {
      return;
    }

    lastFoundSerialRef.current = serial;
  }, [foundRecord]);

  useEffect(() => {
    setAssignLocationError('');
    setAssignLocationMessage('');
    setSelectedRootLocationId('');
    setSelectedLocationId('');
  }, [foundRecord?.id, foundRecord?.currentLocationId]);

  const focusScanner = () => {
    if (isMobile) {
      return;
    }
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const pushHistory = (entry: ScanHistoryEntry) => {
    setScanHistory((current) => [entry, ...current].slice(0, MAX_HISTORY_ITEMS));
  };

  const handleBarcodeChange = (value: string) => {
    setBarcodeInput(value);

    if (scanDebounceRef.current) {
      window.clearTimeout(scanDebounceRef.current);
    }

    const normalized = value.trim();
    if (status === 'scanning' || normalized.length < 6) {
      return;
    }

    scanDebounceRef.current = window.setTimeout(() => {
      runScan(normalized);
    }, 450);
  };

  const runScan = (rawBarcode: string) => {
    const barcode = rawBarcode.trim();

    if (!barcode) {
      setStatus('error');
      setLastBarcode('—');
      setFoundRecord(null);
      pushHistory({
        barcode: '—',
        varibox: '—',
        time: new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
        result: 'error',
      });
      focusScanner();
      return;
    }

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    setStatus('scanning');
    setWorkflowAction(null);

    timeoutRef.current = window.setTimeout(() => {
      void (async () => {
        const time = new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

        try {
          const matchedRecord = await findVariboxByBarcode(barcode);
          setLastBarcode(barcode);

          if (matchedRecord) {
            setStatus('found');
            setFoundRecord(matchedRecord);
            pushHistory({
              barcode,
              varibox: `${matchedRecord.serialNumber} / ${matchedRecord.radexId}`,
              time,
              result: 'found',
            });
          } else {
            setStatus('notFound');
            setFoundRecord(null);
            pushHistory({
              barcode,
              varibox: t('scan.notFoundHistoryValue'),
              time,
              result: 'notFound',
            });
          }
        } catch {
          setStatus('error');
          setFoundRecord(null);
          pushHistory({
            barcode,
            varibox: t('scan.notFoundHistoryValue'),
            time,
            result: 'error',
          });
        }

        setBarcodeInput('');
        focusScanner();
      })();
    }, 350);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    runScan(barcodeInput);
  };

  const startManualEntry = () => {
    setWorkflowAction(null);
    setManualBarcode(barcodeInput.trim());
    setManualEntryOpen(true);
  };

  const closeManualEntry = () => {
    setManualEntryOpen(false);
  };

  const submitManualEntry = () => {
    runScan(manualBarcode);
    setManualEntryOpen(false);
  };

  const handleRootLocationChange = (rootId: string) => {
    setSelectedRootLocationId(rootId);
    setSelectedLocationId('');
    setAssignLocationError('');
    setAssignLocationMessage('');
  };

  const handleAssignLocation = async () => {
    if (!foundRecord?.id || !selectedRootLocationId || !selectedLocationId) {
      return;
    }

    const targetLocationId = selectedLocationId;
    if (targetLocationId === foundRecord.currentLocationId) {
      setAssignLocationMessage(t('scan.assign.noChanges'));
      setAssignLocationError('');
      return;
    }

    setAssigningLocation(true);
    setAssignLocationError('');
    setAssignLocationMessage('');

    try {
      const updated = await assignScannedVariboxLocation(foundRecord.id, targetLocationId);
      setFoundRecord(mapApiVariboxToView(updated));
      setAssignLocationMessage(t('scan.assign.saved'));
    } catch {
      setAssignLocationError(t('scan.assign.error'));
    } finally {
      setAssigningLocation(false);
    }
  };

  const handleWorkflowAction = (action: WorkflowAction) => {
    if (action === 'camera') {
      openCameraScanner();
    }
  };

  const addUnknownVaribox = () => {
    if (isAdmin) {
      navigate('/assets?create=1');
    }
  };

  const openDetails = (showHistory: boolean) => {
    if (!foundRecord) {
      return;
    }

    setDetailsHistoryVisible(showHistory);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
  };

  const resetScan = () => {
    setStatus('ready');
    setFoundRecord(null);
    setLastBarcode('—');
    setWorkflowAction(null);
    closeCameraScanner();
    setSelectedRootLocationId('');
    setSelectedLocationId('');
    setAssigningLocation(false);
    setAssignLocationMessage('');
    setAssignLocationError('');
    setBarcodeInput('');
    lastFoundSerialRef.current = null;
    if (isMobile) {
      openCameraScanner();
    } else {
      focusScanner();
    }
  };

  const renderStatusMessage = () => {
    if (status === 'ready') {
      return t('scan.helper');
    }

    if (status === 'found') {
      return t('scan.foundMessage');
    }

    if (status === 'notFound') {
      return t('scan.notFoundMessage');
    }

    if (status === 'error') {
      return t('scan.errorMessage');
    }

    return t('scan.scanningMessage');
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t('scan.eyebrow')}
        title={t('scan.title')}
        description={t('scan.description')}
        action={
          <Button variant="outlined" onClick={() => navigate('/assets')}>
            {t('scan.buttons.cancel')}
          </Button>
        }
      />

      <Stack spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 4,
            color: '#fff',
            background: 'linear-gradient(145deg, #143f6b 0%, #0f766e 100%)',
            boxShadow: '0 18px 42px rgba(20,63,107,0.2)',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('scan.mobile.title')}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', mt: 0.5 }}>{t('scan.mobile.description')}</Typography>
              </Box>
              <Chip label={statusLabel} color={getStatusColor(status)} sx={{ fontWeight: 700, bgcolor: '#fff' }} />
            </Stack>

            {status === 'scanning' ? <LinearProgress color="inherit" sx={{ borderRadius: 999 }} /> : null}

            {foundRecord ? (
              <Paper sx={{ p: 2, borderRadius: 3, color: 'text.primary' }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.05 }}>{foundRecord.serialNumber}</Typography>
                      <Typography variant="body2" color="text.secondary">{foundRecord.radexId} · {foundRecord.capacityL} L</Typography>
                    </Box>
                    <VariboxStatusBadge status={foundRecord.status} />
                  </Stack>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('scan.movement.currentLocation')}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{foundRecord.lastLocation}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{t('scan.assign.title')}</Typography>
                    <Stack spacing={1.25}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('assets.variboxDetails.rootLocationLabel')}
                        value={selectedRootLocationId}
                        onChange={(event) => handleRootLocationChange(event.target.value)}
                        disabled={assigningLocation}
                      >
                        <MenuItem value="">{t('assets.variboxDetails.rootLocationPlaceholder')}</MenuItem>
                        {rootLocationOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                      </TextField>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('assets.variboxDetails.sublocationLabel')}
                        value={selectedLocationId}
                        onChange={(event) => setSelectedLocationId(event.target.value)}
                        disabled={!selectedRootLocationId || assigningLocation}
                        helperText={!selectedRootLocationId ? t('assets.variboxDetails.sublocationSelectRootHint') : undefined}
                      >
                        <MenuItem value="">{t('scan.assign.chooseSubwarehouse')}</MenuItem>
                        {sublocationOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                      </TextField>
                      {assignLocationMessage ? <Alert severity="success">{assignLocationMessage}</Alert> : null}
                      {assignLocationError ? <Alert severity="error">{assignLocationError}</Alert> : null}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleAssignLocation}
                        disabled={!selectedRootLocationId || !selectedLocationId || assigningLocation}
                        sx={{ minHeight: 48 }}
                      >
                        {assigningLocation ? t('scan.assign.saving') : t('scan.assign.saveAndNext')}
                      </Button>
                    </Stack>
                  </Box>
                  <Button variant="contained" size="large" onClick={resetScan} sx={{ minHeight: 52 }}>
                    {t('scan.mobile.nextScan')}
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="outlined" onClick={() => openDetails(false)}>{t('scan.buttons.details')}</Button>
                    <Button fullWidth variant="outlined" onClick={() => openDetails(true)}>{t('scan.buttons.history')}</Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Stack spacing={1.25}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => handleWorkflowAction('camera')}
                  disabled={status === 'scanning' || cameraLoading}
                  sx={{ minHeight: 58, color: 'primary.dark', bgcolor: '#fff', fontSize: '1rem', fontWeight: 800, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                >
                  {cameraLoading ? t('scan.camera.loading') : t('scan.mobile.cameraButton')}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={startManualEntry}
                  disabled={status === 'scanning'}
                  sx={{ minHeight: 48, color: '#fff', borderColor: 'rgba(255,255,255,0.65)', '&:hover': { borderColor: '#fff' } }}
                >
                  {t('scan.mobile.manualButton')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>

        {cameraError ? <Alert severity="warning">{cameraError}</Alert> : null}
        {status !== 'ready' && status !== 'found' ? (
          <Alert severity={status === 'notFound' ? 'error' : status === 'error' ? 'warning' : 'info'}>
            {renderStatusMessage()}
          </Alert>
        ) : null}
        {status === 'notFound' && isAdmin ? (
          <Button variant="outlined" onClick={addUnknownVaribox}>{t('scan.buttons.addVaribox')}</Button>
        ) : null}

        {scanHistory.length > 0 ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{t('scan.historyTitle')}</Typography>
            <Stack spacing={1}>
              {scanHistory.slice(0, 3).map((entry) => (
                <Stack key={`${entry.barcode}-${entry.time}`} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }} noWrap>{entry.barcode}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{entry.varibox}</Typography>
                  </Box>
                  <Chip size="small" label={t(`scan.historyResults.${entry.result}`)} color={entry.result === 'found' ? 'success' : entry.result === 'notFound' ? 'error' : 'warning'} />
                </Stack>
              ))}
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <Grid container spacing={2.5} sx={{ display: { xs: 'none', sm: 'grid' } }}>
        <Grid item xs={12} lg={7}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              background: 'linear-gradient(180deg, rgba(20,63,107,0.03), rgba(15,118,110,0.02))',
            }}
          >
            <Stack spacing={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {t('scan.scanNowTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('scan.scanNowDescription')}
                  </Typography>
                </Box>
                <Chip label={statusLabel} color={getStatusColor(status)} sx={{ fontWeight: 700 }} />
              </Stack>

              {status === 'scanning' ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    label={t('scan.barcodeLabel')}
                    placeholder={t('scan.barcodePlaceholder')}
                    value={barcodeInput}
                    onChange={(event) => handleBarcodeChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        runScan(barcodeInput);
                      }
                    }}
                    autoComplete="off"
                    InputProps={{
                      sx: {
                        fontSize: { xs: 22, sm: 26 },
                        fontWeight: 800,
                        letterSpacing: 1.6,
                        py: { xs: 1.5, sm: 2 },
                      },
                    }}
                    helperText={t('scan.keyboardHint')}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button type="submit" variant="contained" size="large" sx={{ minWidth: { sm: 180 } }}>
                      {t('scan.buttons.scan')}
                    </Button>
                    <Button variant="outlined" size="large" onClick={startManualEntry}>
                      {t('scan.buttons.manual')}
                    </Button>
                    <Button variant="outlined" size="large" onClick={() => handleWorkflowAction('camera')}>
                      {t('scan.buttons.camera')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {t('scan.lastBarcodeLabel')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                    {lastBarcode}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="text" onClick={resetScan}>
                    {t('scan.buttons.retry')}
                  </Button>
                  <Button variant="text" onClick={startManualEntry}>
                    {t('scan.buttons.manual')}
                  </Button>
                  <Button variant="text" onClick={() => handleWorkflowAction('camera')}>
                    {t('scan.buttons.camera')}
                  </Button>
                </Stack>
              </Stack>

              <Alert severity={status === 'found' ? 'success' : status === 'notFound' ? 'error' : status === 'error' ? 'warning' : 'info'}>
                {renderStatusMessage()}
              </Alert>

              {status === 'notFound' ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderStyle: 'dashed' }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {t('scan.unknownTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('scan.unknownDescription')}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button variant="contained" onClick={resetScan}>{t('scan.buttons.retry')}</Button>
                      <Button variant="outlined" onClick={startManualEntry}>{t('scan.buttons.manual')}</Button>
                      {isAdmin ? <Button variant="outlined" onClick={addUnknownVaribox}>{t('scan.buttons.addVaribox')}</Button> : null}
                    </Stack>
                  </Stack>
                </Paper>
              ) : null}

              {workflowAction ? (
                <Alert severity="info">
                  {t(`scan.workflowActions.${workflowAction}`)}
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {t('scan.foundPanelTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('scan.foundPanelDescription')}
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => handleWorkflowAction('camera')}>
                    {t('scan.buttons.camera')}
                  </Button>
                </Stack>

                {foundRecord ? (
                  <Stack spacing={2.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                          {foundRecord.serialNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {foundRecord.radexId} · {foundRecord.capacityL} L
                        </Typography>
                      </Box>
                      <VariboxStatusBadge status={foundRecord.status} />
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {t('scan.assign.title')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('scan.assign.description')}
                        </Typography>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <TextField
                              select
                              fullWidth
                              label={t('assets.variboxDetails.rootLocationLabel')}
                              value={selectedRootLocationId}
                              onChange={(event) => handleRootLocationChange(event.target.value)}
                            >
                              <MenuItem value="">{t('assets.variboxDetails.rootLocationPlaceholder')}</MenuItem>
                              {rootLocationOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              select
                              fullWidth
                              label={t('assets.variboxDetails.sublocationLabel')}
                              value={selectedLocationId}
                              onChange={(event) => setSelectedLocationId(event.target.value)}
                              disabled={!selectedRootLocationId}
                              helperText={!selectedRootLocationId ? t('assets.variboxDetails.sublocationSelectRootHint') : undefined}
                            >
                              <MenuItem value="">Wybierz podmagazyn</MenuItem>
                              {sublocationOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>

                        {assignLocationMessage ? <Alert severity="success">{assignLocationMessage}</Alert> : null}
                        {assignLocationError ? <Alert severity="error">{assignLocationError}</Alert> : null}

                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            onClick={handleAssignLocation}
                            disabled={!selectedRootLocationId || !selectedLocationId || assigningLocation}
                          >
                            {t('assets.variboxDetails.saveLocation')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1.25}>
                        <Typography variant="caption" color="text.secondary">{t('scan.movement.currentLocation')}</Typography>
                        <Typography variant="h6">{foundRecord.lastLocation}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('assets.headings.status')}: {t(`assets.statuses.${foundRecord.status}`)}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Button variant="contained" onClick={resetScan}>{t('scan.buttons.nextScan')}</Button>
                          <Button variant="outlined" onClick={() => openDetails(false)}>{t('scan.buttons.details')}</Button>
                          <Button variant="outlined" onClick={() => openDetails(true)}>{t('scan.buttons.history')}</Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CircularProgress size={36} sx={{ mb: 2, opacity: 0.75 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {t('scan.waitingState')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('scan.waitingHint')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {t('scan.historyTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('scan.historyDescription')}
                  </Typography>
                </Box>

                {scanHistory.length > 0 ? (
                  <Stack spacing={1.25}>
                    {scanHistory.map((entry) => (
                      <Paper key={`${entry.barcode}-${entry.time}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(20,63,107,0.02)' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{entry.barcode}</Typography>
                            <Typography variant="body2" color="text.secondary">{entry.varibox}</Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary">{entry.time}</Typography>
                            <Chip
                              label={t(`scan.historyResults.${entry.result}`)}
                              color={entry.result === 'found' ? 'success' : entry.result === 'notFound' ? 'error' : 'warning'}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('scan.historyEmpty')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

          </Stack>
        </Grid>
      </Grid>

      <VariboxDetailsDialog
        open={detailsOpen}
        record={foundRecord}
        onClose={closeDetails}
        defaultHistoryVisible={detailsHistoryVisible}
      />

      <Dialog open={cameraOpen} onClose={closeCameraScanner} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>{t('scan.camera.title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t(cameraMode === 'barcode' ? 'scan.camera.barcodeDescription' : 'scan.camera.qrDescription')}
            </Typography>

            <ToggleButtonGroup
              exclusive
              fullWidth
              color="primary"
              value={cameraMode}
              onChange={(_, nextMode: CameraScanMode | null) => {
                if (nextMode) {
                  setCameraMode(nextMode);
                }
              }}
              aria-label={t('scan.camera.modeLabel')}
            >
              <ToggleButton value="barcode">{t('scan.camera.barcodeMode')}</ToggleButton>
              <ToggleButton value="qr">{t('scan.camera.qrMode')}</ToggleButton>
            </ToggleButtonGroup>

            <Paper
              variant="outlined"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                minHeight: isMobile ? '58vh' : 280,
                backgroundColor: 'rgba(15,23,42,0.92)',
              }}
            >
              <Box
                component="video"
                ref={cameraVideoRef}
                muted
                playsInline
                autoPlay
                sx={{
                  display: 'block',
                  width: '100%',
                  height: isMobile ? '68vh' : 320,
                  objectFit: 'cover',
                }}
              />

              {cameraLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'linear-gradient(180deg, rgba(15,23,42,0.65), rgba(15,23,42,0.25))',
                  }}
                >
                  <Stack alignItems="center" spacing={1}>
                    <CircularProgress color="inherit" size={28} />
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      {t('scan.camera.loading')}
                    </Typography>
                  </Stack>
                </Box>
              ) : null}
            </Paper>

            {cameraError ? <Alert severity="warning">{cameraError}</Alert> : null}

            <Alert severity="info">{t('scan.camera.tip')}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCameraScanner}>{t('scan.camera.close')}</Button>
          <Button variant="contained" onClick={restartCameraScanner} disabled={cameraLoading}>
            {t('scan.camera.restart')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualEntryOpen} onClose={closeManualEntry} fullWidth maxWidth="sm">
        <DialogTitle>Wprowadź ręcznie</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Wpisz lub wklej numer seryjny Variboxa, a potem uruchom skan bez czytnika.
            </Typography>
            <TextField
              inputRef={manualBarcodeRef}
              fullWidth
              label={t('scan.barcodeLabel')}
              placeholder={t('scan.barcodePlaceholder')}
              value={manualBarcode}
              onChange={(event) => setManualBarcode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitManualEntry();
                }
              }}
              autoComplete="off"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeManualEntry}>Anuluj</Button>
          <Button variant="contained" onClick={submitManualEntry} disabled={!manualBarcode.trim()}>
            Skanuj
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}